import React, { useCallback, useEffect, useState, useRef } from "react";
import { createEmbeddingContext } from "amazon-quicksight-embedding-sdk";
import { SIGNOL_STAGE } from "@src/constants/constants";
import { useManagerContext } from "@src/context/managerContext";
import { useAuthContext } from "@src/context";
import ManagerHooks from "@src/context/hooks/managerHooks";
import AppUtils from "@src/utils/appUtils";

import Divider from "../Divider";
import Loader from "../Loader";
import Icon from "../Icon";
import ImageWithRetry from "../ImageWithRetry";

const QuicksightDashboard = ({
  hideErrors = false,
  hideTitle = false,
  hideDivider = false,
}) => {
  const { profile, selectedBehaviour } = useManagerContext();
  const auth = useAuthContext();
  const [attempts, setAttempts] = useState(0);

  const [quicksightDashboard, setQuicksightDashboard] = useState(null);
  const quicksightContainerRef = useRef(null);
  const {
    data: { dashboardEmbedUrl } = {},
    isPending: isPendingUrl,
    error: errorGetUrl,
  } = ManagerHooks.useGetQuickSightDashboardUrl(
    auth.activeRole,
    selectedBehaviour.behaviourAcronym,
  );

  const state =
    AppUtils.isMockedApp || SIGNOL_STAGE === "local"
      ? "demo"
      : !!errorGetUrl
        ? "error"
        : isPendingUrl
          ? "loading"
          : quicksightDashboard
            ? "loaded"
            : "loading";

  const embedQuicksight = useCallback(async () => {
    const container = quicksightContainerRef?.current;

    //Validation
    if (errorGetUrl) {
      console.warn("Cannot embed QuickSight, server error ", errorGetUrl);
      return false;
    }
    if (!dashboardEmbedUrl) {
      console.warn("Cannot embed QuickSight, URL missing");
      return false;
    }
    if (container == null) {
      console.warn("Cannot embed QuickSight, container element missing");
      return false;
    }

    const { embedDashboard } = await createEmbeddingContext();
    container.innerHTML = ""; //clear existing dashboard

    try {
      const dashboard = await embedDashboard(
        {
          url: dashboardEmbedUrl,
          container,
          height: "850px",
          width: "100%",
        },
        {
          locale: "en-US",
          parameters: [
            {
              Name: "managerId",
              Values: [auth.user.id],
            },
            {
              Name: "username",
              Values: [auth.user.username],
            },
            {
              Name: "customerCode",
              Values: [profile.customer.customerCode],
            },
            {
              Name: "behaviourSelected",
              Values: [selectedBehaviour.behaviourAcronym],
            },
          ],
          toolbarOptions: {
            undoRedo: true,
            reset: true,
            export: true,
          },
        },
      );

      setQuicksightDashboard(dashboard);
      return dashboard;
    } catch (e) {
      console.error("Error embedding QuickSight dashboard", e);
      return false;
    }
  }, [
    errorGetUrl,
    dashboardEmbedUrl,
    auth.user.id,
    auth.user.username,
    profile.customer.customerCode,
    selectedBehaviour,
  ]);

  useEffect(() => {
    if (
      !errorGetUrl &&
      dashboardEmbedUrl &&
      quicksightContainerRef.current &&
      attempts === 0
    ) {
      if (!errorGetUrl && dashboardEmbedUrl && quicksightContainerRef.current) {
        embedQuicksight();
      }
    }
  }, [
    errorGetUrl,
    dashboardEmbedUrl,
    attempts,
    embedQuicksight,
    selectedBehaviour,
  ]);

  return (
    <section className="m-6">
      {state === "demo" && <div className="bg-white">{getDemoCharts()}</div>}
      {state === "loading" && <Loader size="2xl" />}
      {state === "loaded" && hideTitle !== false && (
        <div className="flex flex-col items-center">
          <div className="font-extrabold text-2xl md:text-4xl m-0 mb-6">
            Fuel Analysis
          </div>
        </div>
      )}

      {state !== "demo" && <div ref={quicksightContainerRef}></div>}
      {state === "error" && hideErrors !== true && (
        <h5 className="text-error text-center">
          <Icon name="brokenChains" size="xl" inline />
          <span className="mx-3" title={errorGetUrl}>
            An unexpected error occurred. Please reload the page and try again
          </span>
        </h5>
      )}

      {state === "loaded" && hideDivider !== false && <Divider />}
    </section>
  );
};

export default QuicksightDashboard;

function getDemoCharts() {
  const graphUrl =
    "https://signol-email-images.s3.eu-west-2.amazonaws.com/demo-manager-dashboard/graph{{X}}.png";
  const addImage = (i) => (
    <ImageWithRetry
      src={graphUrl.replace("{{X}}", i)}
      alt={"Graph " + i}
      style={{ height: "fit-content" }}
      className="inline-block"
    />
  );
  return (
    <div className="p-6 text-center">
      {addImage(1)}
      {addImage(2)}
      <div className="inline-flex flex-col">
        {addImage(3)}
        {addImage(4)}
      </div>
      {addImage(5)}
      <div className="inline-flex flex-col">
        {addImage(6)}
        {addImage(7)}
      </div>
      {addImage(8)}
      <div className="inline-flex flex-col">
        {addImage(9)}
        {addImage(10)}
      </div>
      {addImage(11)}
    </div>
  );
}
